import emit from '../../../common/events';
import environment from '../../../common/environment';
import store from '../../../store/store';
import {  selectSelectedCatalog } from '../../search/store/search.selector';
import { CoverImageAnalyticsConstants } from '../../../common/constants/anayltics.constants';
import { OfferTypeChecker } from '../../../common/utils/offer-type-util';
import { ProductCardData, ProductSmallCardData } from '../product-card-display';
import { StoreState } from '../../../store/store.interface';
import {
  determineBusinessUnit,
  getPdpPageAnalytics,
  PRODUCT_TYPE_FLASHCARD,
  PROFESSIONAL_FCD_TITLE_TYPE,
} from '../../../common/utils/analytics-util';
import { formattedJoin, HE_LOWER, isHeStorePurchaseFlow, SEG_LOWER } from '../../../common/utils/util';
import { getProductDetailsForAnalytics, isProfessional } from '../../../common/utils/product-util';
import { getProgramUrl } from './product-card.util';
import { getUserDataForUserAnalytics } from '../../user-profile/user-profile.selectors';
import { getHeStoreDataForAnalytics } from '../../create-custom/utils/create-custom-product.util';
import { getCartAnalyticsOverrides } from '../../cart/shop-cart/shop-cart.analytics';
import { getPromoCodeState } from '../../cart/shop-cart/store/shop-cart.selectors';
import { getSharpenPromoAnalytics } from '../../upsell/sharpen-upsell-promo.analytics';

/**
 * Emits the product view analytics event.
 *
 * @param element
 * @param productData
 * @param lowercaseBusinessUnit
 * @param componentName
 */
export function emitProductAnalyticsEvent(
  element: Element,
  productData: ProductCardData,
  lowercaseBusinessUnit: string,
  componentName?: string
): void {
  const state = store.getState() as StoreState;
  const catalog = selectSelectedCatalog(state);
  emit({
    element: element,
    name: 'view-product-details',
    detail: {
      ...(componentName && { comp: componentName }),
      label: 'View Product Details',
      catalog: catalog,
      user: getUserDataForUserAnalytics(state),
      productDetail: getProductDetailsForAnalytics(productData, lowercaseBusinessUnit),
      searchText: getPdpPageAnalytics(productData?.programSourceId! || productData.id),
      ...getSharpenPromoAnalytics(
        [productData?.programSourceId ?? ''],
        getPromoCodeState(state)?.coupon,
      ),
    },
  });
}

/**
 * This is called from the product card display component.
 * TODO: see if it can be merged with the above function, the event is the same but product data differs.
 */
export function emitProductViewAnalyticsEvent(
  element: Element,
  productInfo: ProductCardData,
  storeCategory?: string,
  selectedCatalog?: string,
  orderType?: string
): void {
  const state = store.getState() as StoreState;

  emit({
    element: element,
    name: 'view-product-details',
    detail: {
      label: 'View Product Details',
      user: getUserDataForUserAnalytics(state),
      store: storeCategory,
      catalog: selectedCatalog ?? selectSelectedCatalog(state),
      productDetail: JSON.parse(JSON.stringify(buildProductDetailsModel(productInfo))),
      searchText: getPdpPageAnalytics(productInfo?.programSourceId! || productInfo.id),
      ...getCartAnalyticsOverrides(state, orderType),
    },
  });
}

/**
 * This function was originally a part of the product card display component.
 * TODO: see if it can be merged with getProductDetailsForAnalytics.
 *
 * @param productInfo
 */
function buildProductDetailsModel(productInfo: ProductCardData): any {
  const productBusinessUnit = determineBusinessUnit(
    (productInfo.productBusinessUnit ?? productInfo.businessUnit ?? '').toUpperCase()
  );
  const productDetail: any = {
    title: productInfo.title,
    copyRightYear: productInfo.copyRightYear,
    businessUnit: productBusinessUnit,
    instructorOnly: productInfo.instructorOnly,
    store: productInfo.store,
    isbn: productInfo.isbn,
    isbn13: productInfo.isbn13,
    netPrice: productInfo.netPrice,
    description: productInfo.description,
    gradeLevels: productInfo.gradeLevels,
    isPublished: productInfo.isPublished,
    totalPrice: productInfo.totalPrice,
    format: productInfo.format,
    isVirtual: productInfo.isVirtual,
    cardType: productInfo.cardType,
    quantity: productInfo.quantity,
    mhid: productInfo.mhid,
    author: formattedJoin(productInfo.contributors!),
    edition: productInfo.edition,
    productType:
      productInfo.titleType === PROFESSIONAL_FCD_TITLE_TYPE
        ? PRODUCT_TYPE_FLASHCARD
        : productInfo.format,
    coverImage:
      productBusinessUnit === HE_LOWER && OfferTypeChecker.isHeEbook(productInfo?.offerType)
        ? CoverImageAnalyticsConstants.NOT_SHOWN
        : CoverImageAnalyticsConstants.SHOWN,
  };

  if (productInfo.businessUnit?.toLowerCase() === 'he') {
    productDetail.programSourceId = productInfo.programSourceId;
    productDetail.programSourceName = productInfo.programSourceName;
    productDetail.isEvergreenPDP = productInfo.isEvergreen;
  }

  if (isHeStorePurchaseFlow()) {
    Object.assign(productDetail, {
      ...getHeStoreDataForAnalytics({
        isCreateEbook: OfferTypeChecker.isCreateEbook(productInfo?.offerType),
        formatDescription: productInfo?.formatTypeDesc || productInfo?.format,
        formatTypeDescription: productInfo?.format,
      }),
    });
  }
  return productDetail;
}

/**
 * Emits the program family view analytics event.
 *
 * @param element
 * @param productData
 * @param programData
 */
export function emitProgramFamilyAnalyticsEvent(
  element: Element,
  productData: ProductCardData,
  programData?: any
): void {
  const state = store.getState() as StoreState;
  emit({
    element: element,
    name: 'view-program-family-details',
    detail: {
      label: 'View Program Family Details',
      user: getUserDataForUserAnalytics(state),
      productDetail: JSON.parse(
        JSON.stringify({
          title: productData.title,
          copyRightYear: productData.copyRightYear,
          businessUnit: SEG_LOWER,
          isbn: productData.isbn,
          isbn13: productData.isbn13,
          description: productData.description,
          author: formattedJoin(productData.contributors!),
          edition: productData.edition,
          pageCategory: window.mhPageCategory,
          programId: programData?.id || productData.partsOf?.value,
          programTitle: programData?.title || productData.partsOf?.label,
          href: `${environment().mheducationUrl}${getProgramUrl(programData?.id || productData.partsOf?.value)}`,
        })
      ),
    },
  });
}

/**
 * Emits the redeem ebook code analytics event triggered from the cart page.
 */
export function emitRedeemEbookCodeEvent({
  element,
  product,
}: {
  element: Element;
  product: ProductCardData | ProductSmallCardData | undefined;
}): void {
  const state = store.getState() as StoreState;
  emit({
    element: element,
    name: 'redeem-ebook-code',
    detail: {
      label: 'Redeem Ebook Code',
      url: window.location.href,
      user: getUserDataForUserAnalytics(state),
      isbn: product?.isbn,
      isbn13: product?.isbn13,
      productTitle: product?.title,
      ...getCartAnalyticsOverrides(state),
    },
  });
}
